/**
 * iter 373 — StaffAttendanceGate
 *
 * Blocking modal that fires on portal load for in-scope roles (teacher, CM,
 * CA, pre_sales) once local time crosses 9 AM / 10:30 PM.
 *
 * States:
 *  1. first_time            → Register regular location (office/home) first
 *  2. needs_checkin         → Force check-in modal (day plan)
 *  3. needs_checkout        → Force check-out modal (achievements + %)
 *  4. done                  → Nothing to do — component renders null
 *
 * When off-location, requires a reason (goes to manager approval later).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Loader2, Clock, LogIn, LogOut, TrendingUp, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IN_SCOPE = new Set(['teacher', 'center_manager', 'center_admin', 'pre_sales']);

const PLAN_FIELDS = {
  teacher: [
    { key: 'mentorship_sessions', label: 'Mentorship sessions' },
    { key: 'doubts', label: 'Doubt sessions' },
    { key: 'answer_checking', label: 'Answer copies to check' },
    { key: 'test_paper_creation', label: 'Test papers to create' },
    { key: 'video_feedbacks', label: 'Video feedbacks' },
  ],
  pre_sales: [
    { key: 'dial_connections', label: 'Dialled connections' },
    { key: 'walkins', label: 'Walk-ins to generate' },
  ],
  center_manager: [],
  center_admin: [],
};

function localIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const tzMin = -d.getTimezoneOffset();
  const sign = tzMin >= 0 ? '+' : '-';
  const th = pad(Math.floor(Math.abs(tzMin) / 60));
  const tm = pad(Math.abs(tzMin) % 60);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sign}${th}:${tm}`;
}

function getGeo() {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('Geolocation not supported'));
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      err => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  });
}

export default function StaffAttendanceGate({ role }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [geo, setGeo] = useState(null);
  const [geoErr, setGeoErr] = useState(null);
  // Location register
  const [locLabel, setLocLabel] = useState('office');
  // Check-in
  const [plan, setPlan] = useState({});
  const [comments, setComments] = useState('');
  const [freeHours, setFreeHours] = useState('');
  const [offReason, setOffReason] = useState('');
  // iter 385 — Off-location classification: '' | 'save' | 'temporary'
  const [offMode, setOffMode] = useState('');
  const [offNewLabel, setOffNewLabel] = useState('office_2');
  // Check-out
  const [achievement, setAchievement] = useState({});
  const [achievementComments, setAchievementComments] = useState('');
  const [outOffReason, setOutOffReason] = useState('');
  const [outOffMode, setOutOffMode] = useState('');
  const [outOffNewLabel, setOutOffNewLabel] = useState('office_2');
  // iter 384 — Missed-checkout recovery
  const [recoveryAchievement, setRecoveryAchievement] = useState({});
  const [recoveryComments, setRecoveryComments] = useState('');
  const [recoveryReason, setRecoveryReason] = useState('');

  const inScope = IN_SCOPE.has(role);

  const load = useCallback(async () => {
    if (!inScope) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API_URL}/api/attendance/staff/me`, {
        params: { client_local_iso: localIso() },
        withCredentials: true,
      });
      setMe(r.data);
    } catch (e) {
      // Silently ignore — attendance non-blocking on API failure
      setMe(null);
    } finally { setLoading(false); }
  }, [inScope]);

  useEffect(() => { load(); }, [load]);

  // Quick-link on the dashboard can ask the gate to (re)appear / refresh so
  // staff can check in/out proactively without waiting for the auto-popup.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('attendance:open', handler);
    window.addEventListener('attendance:refresh', handler);
    return () => {
      window.removeEventListener('attendance:open', handler);
      window.removeEventListener('attendance:refresh', handler);
    };
  }, [load]);

  // Fetch fresh teacher shift free hours (best-effort). If the backend
  // returned a computed value on /me use that — else leave blank for the
  // teacher to fill manually.
  useEffect(() => {
    if (!me?.in_scope) return;
    if (role === 'teacher' && me.computed_free_hours != null) {
      setFreeHours(String(me.computed_free_hours));
    }
  }, [me, role]);

  const grabGeo = async () => {
    setGeoErr(null);
    try { setGeo(await getGeo()); }
    catch (e) { setGeoErr(e.message || 'Failed to get location'); }
  };

  if (loading || !me || !me.in_scope) return null;

  // iter 384 — Recovery has highest priority. Oldest pending day first.
  const pending = me.pending_recovery || [];
  const recoveryDay = pending.length > 0 ? pending[0] : null;

  // Compute stage
  const first_time = me.first_time;
  const today = me.today;
  const checkedIn = !!today?.check_in?.ts;
  const checkedOut = !!today?.check_out?.ts;
  let stage = null;
  if (recoveryDay) stage = 'recovery';
  else if (first_time && me.window.checkin_open) stage = 'register';
  else if (!checkedIn && me.window.checkin_open) stage = 'checkin';
  else if (checkedIn && !checkedOut && me.window.checkout_open) stage = 'checkout';
  if (!stage) return null;

  // Compute if off-location
  const isOff = geo && (me.regular_locations || []).length > 0 && !(me.regular_locations || []).some(L => {
    const d = distanceMeters(geo.lat, geo.lng, L.lat, L.lng);
    return d <= 100;
  });

  const submitLocation = async () => {
    if (!geo) return toast.error('Please share your location first');
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/attendance/staff/locations/register`,
        { label: locLabel, lat: geo.lat, lng: geo.lng, is_regular: true },
        { withCredentials: true });
      toast.success(`${locLabel} location saved`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save location');
    } finally { setBusy(false); }
  };

  const submitCheckIn = async () => {
    if (!geo) return toast.error('Please share your location first');
    if (isOff && offMode === '') return toast.error('Please classify this location (WFH / Office / Temporary)');
    const asksReason = isOff && offMode !== 'save';
    if (asksReason && !offReason.trim()) return toast.error('Please explain why you are away from your regular location');
    setBusy(true);
    try {
      const payload = {
        lat: geo.lat, lng: geo.lng,
        client_local_iso: localIso(),
        free_hours: freeHours ? Number(freeHours) : null,
        off_location_reason: asksReason ? offReason.trim() : (offMode === 'save' ? `Saving as ${offNewLabel}` : null),
        plan: { ...plan, comments: comments.trim() },
      };
      if (isOff && offMode === 'save') payload.new_location = { label: offNewLabel };
      const r = await axios.post(`${API_URL}/api/attendance/staff/check-in`, payload, { withCredentials: true });
      toast.success(r.data.is_off_location ? 'Checked in — awaiting manager approval' : 'Checked in');
      setOffMode(''); setOffReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check in');
    } finally { setBusy(false); }
  };

  const submitCheckOut = async () => {
    if (!geo) return toast.error('Please share your location first');
    if (isOff && outOffMode === '') return toast.error('Please classify this location (WFH / Office / Temporary)');
    const asksReason = isOff && outOffMode !== 'save';
    if (asksReason && !outOffReason.trim()) return toast.error('Please explain why you are away from your regular location');
    setBusy(true);
    try {
      const payload = {
        lat: geo.lat, lng: geo.lng,
        client_local_iso: localIso(),
        off_location_reason: asksReason ? outOffReason.trim() : (outOffMode === 'save' ? `Saving as ${outOffNewLabel}` : null),
        achievement: { ...achievement, comments: achievementComments.trim() },
      };
      if (isOff && outOffMode === 'save') payload.new_location = { label: outOffNewLabel };
      const r = await axios.post(`${API_URL}/api/attendance/staff/check-out`, payload, { withCredentials: true });
      toast.success(`Checked out · ${r.data.percent_achieved != null ? r.data.percent_achieved + '% achieved' : 'notes saved'}`);
      setOutOffMode(''); setOutOffReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check out');
    } finally { setBusy(false); }
  };

  const submitRecovery = async () => {
    if (!recoveryDay) return;
    if (!recoveryReason.trim()) return toast.error('Please explain why you missed the check-out');
    setBusy(true);
    try {
      const payload = {
        date: recoveryDay.date,
        client_local_iso: localIso(),
        reason: recoveryReason.trim(),
        achievement: { ...recoveryAchievement, comments: recoveryComments.trim() },
      };
      const r = await axios.post(`${API_URL}/api/attendance/staff/late-check-out`, payload, { withCredentials: true });
      toast.success(`Recovered ${recoveryDay.date}${r.data.percent_achieved != null ? ` · ${r.data.percent_achieved}% achieved` : ''}`);
      setRecoveryAchievement({});
      setRecoveryComments('');
      setRecoveryReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to recover attendance');
    } finally { setBusy(false); }
  };

  const planFields = PLAN_FIELDS[role] || [];

  return (
    <Dialog open modal>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="staff-attendance-gate">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {stage === 'recovery' && <><AlertCircle className="w-5 h-5 text-rose-400" />Recover missed check-out — {recoveryDay?.date}</>}
            {stage === 'register' && <><MapPin className="w-5 h-5 text-amber-400" />Save your regular location</>}
            {stage === 'checkin' && <><LogIn className="w-5 h-5 text-cyan-400" />Check in for the day</>}
            {stage === 'checkout' && <><LogOut className="w-5 h-5 text-emerald-400" />Check out for the day</>}
          </DialogTitle>
        </DialogHeader>

        {/* Geo capture block — hidden during recovery (no location needed for past days) */}
        {stage !== 'recovery' && (
        <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-400" />
              <span className="text-slate-300">Location</span>
            </div>
            {geo ? (
              <span className="text-emerald-300 text-xs">✓ {geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}</span>
            ) : (
              <Button size="sm" variant="outline" onClick={grabGeo} className="border-cyan-500/40 text-cyan-300" data-testid="attendance-share-location">
                Share location
              </Button>
            )}
          </div>
          {geoErr && <p className="text-rose-300 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" />{geoErr}</p>}
          {geo && (me.regular_locations || []).length > 0 && (
            isOff
              ? <p className="text-amber-300 text-xs">Not at your regular location — a reason will be needed and this will go for manager approval.</p>
              : <p className="text-emerald-300 text-xs">✓ You're at your regular location.</p>
          )}
        </div>
        )}

        {/* Stage: recovery — missed check-out yesterday (iter 384) */}
        {stage === 'recovery' && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3">
              <p className="text-rose-100 text-sm font-medium">
                You did not check out on <span className="font-bold">{recoveryDay?.date}</span>.
              </p>
              <p className="text-rose-200/80 text-xs mt-1">
                Because of this, that day is currently marked <strong>ABSENT</strong>. Fill your achieved numbers and the reason for the missed check-out to mark it PRESENT.
              </p>
              {pending.length > 1 && (
                <p className="text-amber-200 text-xs mt-2">
                  You have {pending.length} pending days to recover. We'll walk through them oldest → newest.
                </p>
              )}
            </div>

            {(PLAN_FIELDS[role] || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-slate-400 text-xs">What did you actually achieve on {recoveryDay?.date}?</p>
                {(PLAN_FIELDS[role] || []).map(f => (
                  <div key={f.key} className="grid grid-cols-3 gap-2 items-center">
                    <Label className="text-slate-400 text-xs">{f.label}</Label>
                    <div className="text-slate-300 text-sm text-center">
                      <span className="text-slate-500 text-xs">Planned</span>{' '}
                      <strong>{recoveryDay?.plan?.[f.key] ?? 0}</strong>
                    </div>
                    <Input
                      type="number" min={0}
                      placeholder="Achieved"
                      value={recoveryAchievement[f.key] ?? ''}
                      onChange={(e) => setRecoveryAchievement({ ...recoveryAchievement, [f.key]: Number(e.target.value) || 0 })}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid={`recovery-achievement-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-slate-400 text-xs">
                {(PLAN_FIELDS[role] || []).length ? 'End-of-day notes' : 'What did you achieve on this day?'}
              </Label>
              <Textarea rows={2} value={recoveryComments} onChange={(e) => setRecoveryComments(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white" data-testid="recovery-comments" />
            </div>
            <div>
              <Label className="text-rose-300 text-xs">Why did you miss the check-out? *</Label>
              <Textarea rows={2} value={recoveryReason} onChange={(e) => setRecoveryReason(e.target.value)}
                placeholder="e.g. Left the office in a rush for a client meeting and forgot to check out."
                className="bg-rose-500/5 border-rose-500/30 text-white" data-testid="recovery-reason" />
            </div>
            <Button onClick={submitRecovery} disabled={busy || !recoveryReason.trim()}
              className="w-full bg-rose-600 hover:bg-rose-700" data-testid="submit-recovery-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <LogOut className="w-4 h-4 mr-1" />}
              Recover {recoveryDay?.date}
            </Button>
          </div>
        )}

        {/* Stage: register */}
        {stage === 'register' && (
          <div className="space-y-3 text-sm">
            {me.assigned_centers_count > 1 ? (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 space-y-1">
                <p className="text-amber-200 text-sm font-medium">You are assigned to <strong>{me.assigned_centers_count} centers</strong>.</p>
                <p className="text-amber-100/80 text-xs">
                  Pick <strong>Office 1</strong> if you're at your primary center today, <strong>Office 2</strong> if you're at your secondary center, or <strong>WFH (Home)</strong> if you're working from home. You can add the second office the next time you check in from it.
                </p>
              </div>
            ) : (
              <p className="text-slate-400 text-xs">First time here — tell us where you're checking in from today so we can save it as your regular location for future days.</p>
            )}
            <div>
              <Label className="text-slate-400 text-xs">This location is my</Label>
              <Select value={locLabel} onValueChange={setLocLabel}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="loc-label-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="office">Office 1</SelectItem>
                  <SelectItem value="office_2">Office 2</SelectItem>
                  <SelectItem value="home">WFH (Home)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={submitLocation} disabled={busy || !geo} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="save-regular-location-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save and continue
            </Button>
          </div>
        )}

        {/* Stage: check-in — day plan */}
        {stage === 'checkin' && (
          <div className="space-y-3 text-sm">
            {role === 'teacher' && (
              <div className="rounded-lg bg-cyan-500/10 border border-cyan-500/30 p-2 text-xs flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-300" />
                <span className="text-slate-200">Non-lecture free hours today:</span>
                <Input value={freeHours} onChange={(e) => setFreeHours(e.target.value)} className="bg-slate-800 border-slate-700 h-7 w-16 text-white" data-testid="checkin-free-hours" />
                <span className="text-slate-400">h</span>
                {me.computed_free_hours != null && (
                  <span className="text-emerald-300 text-[10px]">
                    auto (shift − classes − doubts)
                  </span>
                )}
              </div>
            )}
            {planFields.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {planFields.map(f => (
                  <div key={f.key}>
                    <Label className="text-slate-400 text-xs">{f.label}</Label>
                    <Input
                      type="number" min={0}
                      value={plan[f.key] ?? ''}
                      onChange={(e) => setPlan({ ...plan, [f.key]: Number(e.target.value) || 0 })}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid={`plan-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-slate-400 text-xs">Comments / plan notes</Label>
              <Textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="checkin-comments" />
            </div>
            {isOff && (
              <OffLocationClassifier
                mode={offMode}
                setMode={setOffMode}
                newLabel={offNewLabel}
                setNewLabel={setOffNewLabel}
                reason={offReason}
                setReason={setOffReason}
                officeSlotsRemaining={me.office_slots_remaining ?? 0}
                hasHomeSaved={me.has_home_saved}
                testIdPrefix="checkin"
              />
            )}
            <Button onClick={submitCheckIn} disabled={busy || !geo} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="submit-checkin-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <LogIn className="w-4 h-4 mr-1" />}
              Check In
            </Button>
          </div>
        )}

        {/* Stage: check-out — achievements */}
        {stage === 'checkout' && (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2 text-xs">
              <p className="text-slate-400">Fill in what you achieved against your plan. Overall % will be highlighted after submit.</p>
            </div>
            {planFields.length > 0 && (
              <div className="space-y-2">
                {planFields.map(f => (
                  <div key={f.key} className="grid grid-cols-3 gap-2 items-center">
                    <Label className="text-slate-400 text-xs">{f.label}</Label>
                    <div className="text-slate-300 text-sm text-center">
                      <span className="text-slate-500 text-xs">Planned</span>{' '}<strong>{today?.plan?.[f.key] || 0}</strong>
                    </div>
                    <Input
                      type="number" min={0}
                      placeholder="Achieved"
                      value={achievement[f.key] ?? ''}
                      onChange={(e) => setAchievement({ ...achievement, [f.key]: Number(e.target.value) || 0 })}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid={`achievement-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-slate-400 text-xs">
                {planFields.length ? 'End-of-day notes' : 'What did you achieve today?'}
              </Label>
              <Textarea rows={3} value={achievementComments} onChange={(e) => setAchievementComments(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white" data-testid="checkout-comments" />
            </div>
            {isOff && (
              <OffLocationClassifier
                mode={outOffMode}
                setMode={setOutOffMode}
                newLabel={outOffNewLabel}
                setNewLabel={setOutOffNewLabel}
                reason={outOffReason}
                setReason={setOutOffReason}
                officeSlotsRemaining={me.office_slots_remaining ?? 0}
                hasHomeSaved={me.has_home_saved}
                testIdPrefix="checkout"
              />
            )}
            <div className="text-xs text-slate-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Planned earlier: <span className="text-slate-200">{today?.plan?.comments ? '"' + today.plan.comments + '"' : '—'}</span></div>
            <Button onClick={submitCheckOut} disabled={busy || !geo} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-checkout-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <LogOut className="w-4 h-4 mr-1" />}
              Check Out
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * iter 385 — Ask employees to classify an unusual check-in/out location:
 *   - "This is my WFH / Office 1 / Office 2" → save as a regular location
 *   - "Away temporarily (client visit / travel)" → free-text reason
 */
function OffLocationClassifier({ mode, setMode, newLabel, setNewLabel, reason, setReason, officeSlotsRemaining, hasHomeSaved, testIdPrefix }) {
  const canAddOffice = officeSlotsRemaining > 0;
  const canAddHome = !hasHomeSaved;
  return (
    <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 space-y-2">
      <p className="text-amber-100 text-xs font-medium">You're not at any of your saved locations. Tell us what this place is:</p>
      <div className="grid grid-cols-1 gap-2">
        {(canAddOffice || canAddHome) && (
          <label className="flex items-start gap-2 text-xs text-amber-100 cursor-pointer">
            <input type="radio" name={`${testIdPrefix}-off-mode`} checked={mode === 'save'} onChange={() => setMode('save')} data-testid={`${testIdPrefix}-mode-save`} className="mt-0.5" />
            <span>
              This is a regular location — save it as:
              <select
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onClick={() => setMode('save')}
                className="ml-2 bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1"
                data-testid={`${testIdPrefix}-new-label`}
              >
                {canAddOffice && <option value="office">Office 1</option>}
                {canAddOffice && <option value="office_2">Office 2</option>}
                {canAddHome && <option value="home">WFH (Home)</option>}
              </select>
              {!canAddOffice && <span className="ml-2 text-amber-300/60">(2 offices already saved — ask HR to remove one to add a new office)</span>}
            </span>
          </label>
        )}
        <label className="flex items-start gap-2 text-xs text-amber-100 cursor-pointer">
          <input type="radio" name={`${testIdPrefix}-off-mode`} checked={mode === 'temporary'} onChange={() => setMode('temporary')} data-testid={`${testIdPrefix}-mode-temporary`} className="mt-0.5" />
          <span>Just away temporarily today (client visit, travel, family emergency, etc.)</span>
        </label>
      </div>
      {mode === 'temporary' && (
        <div className="pt-1">
          <Label className="text-amber-300 text-xs">Reason *</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="bg-amber-500/5 border-amber-500/30 text-white" data-testid={`${testIdPrefix}-off-reason`} />
          <p className="text-amber-300/70 text-[10px] mt-1">This attendance will go to your reporting manager for approval.</p>
        </div>
      )}
    </div>
  );
}

function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (v) => v * Math.PI / 180;
  const dp = toRad(lat2 - lat1), dl = toRad(lng2 - lng1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
